/* ===== CATI Pakistan — Dynamic Content Renderer ===== */
import { getData, getDefaults, resolveImageUrl } from './siteData.js';
import { revealAllPending } from './scroll-reveal.js';
import { initCarousel } from './carousel.js';
import { setupCachedVideo } from './videoCache.js';

let isSyncedListenerAdded = false;

export function initRenderer() {
  renderCourses();
  renderGallery();
  renderNews();
  renderEnquiryForm();
  renderHero();
  renderSchools();
  renderStats();
  renderAbout();
  renderQuickLinks();

  setTimeout(() => {
    try { if (typeof revealAllPending === 'function') revealAllPending(); } catch (e) {}
  }, 30);

  if (!isSyncedListenerAdded && typeof window !== 'undefined') {
    isSyncedListenerAdded = true;
    window.addEventListener('cati-data-synced', () => {
      try {
        renderCourses();
        renderGallery();
        renderNews();
        renderEnquiryForm();
        renderHero();
        renderSchools();
        renderStats();
        renderAbout();
        renderQuickLinks();
        setTimeout(() => {
          try { if (typeof revealAllPending === 'function') revealAllPending(); } catch (e) {}
        }, 30);
      } catch (e) {
        console.warn('Re-render notice:', e);
      }
    });
  }
}

/* ---------- HELPERS ---------- */
function formatDate(dateStr) {
  if (!dateStr) return { day: '--', month: 'TBD', full: 'Schedule TBD' };
  try {
    let cleanStr = String(dateStr).trim();
    if (!cleanStr.includes('T') && cleanStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      cleanStr += 'T00:00:00';
    }
    const d = new Date(cleanStr);
    if (isNaN(d.getTime())) {
      return { day: '--', month: 'TBD', full: String(dateStr) || 'Schedule TBD' };
    }
    const day = d.getDate().toString().padStart(2, '0');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const month = months[d.getMonth()] || 'TBD';
    return { day, month, full: `${day} ${month} ${d.getFullYear()}` };
  } catch (e) {
    return { day: '--', month: 'TBD', full: String(dateStr) || 'Schedule TBD' };
  }
}

function formatPrice(amount) {
  if (!amount && amount !== 0) return '';
  return amount.toLocaleString();
}

function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/* ---------- COURSES ---------- */
function getCourseStatus(startDateStr, endDateStr) {
  const todayStr = new Date().toISOString().slice(0, 10);
  let statusKey = 'ongoing';
  let label = 'On-going';
  let icon = 'fa-circle-play';
  let badgeClass = 'status-ongoing';

  try {
    if (endDateStr && endDateStr < todayStr) {
      statusKey = 'completed';
      label = 'Completed';
      icon = 'fa-circle-check';
      badgeClass = 'status-completed';
    } else if (startDateStr && startDateStr > todayStr) {
      statusKey = 'upcoming';
      label = 'Upcoming';
      icon = 'fa-calendar-day';
      badgeClass = 'status-upcoming';
    }
  } catch (e) {}

  return { key: statusKey, label, icon, badgeClass };
}

function renderCourseCard(course, showEnroll = true) {
  const start = formatDate(course.startDate);
  const end = formatDate(course.endDate);
  const statusInfo = getCourseStatus(course.startDate, course.endDate);

  return `
    <div class="course-card reveal" data-school="${course.school}" data-status="${statusInfo.key}">
      <div class="course-card__img">
        <img src="${resolveImageUrl(course.image)}" alt="${escapeHtml(course.title)}" loading="lazy">
        <div class="course-card__date">
          <span class="course-card__date-day">${start.day}</span>
          <span class="course-card__date-month">${start.month}</span>
        </div>
        <div class="course-card__status-badge ${statusInfo.badgeClass}">
          <i class="fa-solid ${statusInfo.icon}"></i> ${statusInfo.label}
        </div>
      </div>
      <div class="course-card__school">${escapeHtml(course.schoolLabel)}</div>
      <div class="course-card__body">
        <h4 class="course-card__title"><a href="course-details.html?id=${course.id}" style="color:inherit;text-decoration:none">${escapeHtml(course.title)}</a></h4>
        <div class="course-card__meta">
          <div class="course-card__meta-item"><i class="fa-regular fa-calendar"></i> ${start.full} – ${end.full}</div>
          <div class="course-card__meta-item"><i class="fa-regular fa-clock"></i> ${course.durationWeeks} week${course.durationWeeks !== 1 ? 's' : ''}</div>
        </div>
        ${(course.pricePKR || course.priceUSD) ? `
        <div class="course-card__price">
          ${course.pricePKR ? `<span class="course-card__price-amount">PKR ${formatPrice(course.pricePKR)}</span>` : ''}
          ${course.priceUSD ? `<span class="course-card__price-currency">/ USD ${formatPrice(course.priceUSD)}</span>` : ''}
        </div>` : ''}
        <a href="course-details.html?id=${course.id}" class="btn btn--primary btn--sm" style="width:100%;justify-content:center;margin-top:0.5rem;font-weight:var(--fw-semibold)">
          View Course Details <i class="fa-solid fa-arrow-right" style="margin-left:0.4rem"></i>
        </a>
      </div>
    </div>`;
}

function renderCourses() {
  let courses = getData('courses');
  if (!courses || !Array.isArray(courses) || courses.length === 0) {
    courses = getDefaults('courses') || [];
  }

  // Courses page grid
  const courseGrid = document.getElementById('courseGrid');
  if (courseGrid) {
    if (courses.length === 0) {
      courseGrid.innerHTML = '<div style="grid-column:1/-1;text-align:center;padding:3rem">No courses available.</div>';
    } else {
      courseGrid.innerHTML = courses.map(c => {
        try {
          return renderCourseCard(c, true);
        } catch (err) {
          console.error('Error rendering course:', c, err);
          return '';
        }
      }).join('');
    }
    window.dispatchEvent(new CustomEvent('catiCoursesRendered'));
  }

  // Homepage course carousel
  const courseCarousel = document.querySelector('.course-carousel');
  if (courseCarousel) {
    courseCarousel.innerHTML = courses.slice(0, 6).map(c => {
      try {
        return renderCourseCard(c, true);
      } catch (err) {
        return '';
      }
    }).join('');
  }
}

/* ---------- GALLERY ---------- */
function renderGallery() {
  const gallery = getData('gallery');
  if (!gallery) return;

  // All gallery grids on any page
  document.querySelectorAll('.gallery-grid[data-dynamic]').forEach(grid => {
    grid.innerHTML = gallery.map(img => `
      <div class="gallery-item">
        <img src="${img.src}" alt="${escapeHtml(img.alt)}" loading="lazy">
      </div>
    `).join('');
  });
}

/* ---------- NEWS ---------- */
function renderNews() {
  const news = getData('news');
  if (!news) return;

  const todayStr = new Date().toISOString().slice(0, 10);
  const activeNews = news.filter(item => {
    if (item.active === false) return false;
    if (item.expiryDate && item.expiryDate < todayStr) return false;
    return true;
  });

  document.querySelectorAll('.news-grid[data-dynamic]').forEach(grid => {
    grid.innerHTML = activeNews.map(item => `
      <div class="event-card reveal">
        <div class="event-card__img img-zoom">
          <img src="${item.image}" alt="${escapeHtml(item.title)}" loading="lazy">
        </div>
        <div class="event-card__body">
          <div class="event-card__date">${escapeHtml(item.date)}</div>
          <h3 class="event-card__title">${escapeHtml(item.title)}</h3>
          ${item.description ? `<p style="font-size:var(--fs-small);color:var(--clr-text-muted);margin-top:var(--space-sm)">${escapeHtml(item.description)}</p>` : ''}
        </div>
      </div>
    `).join('');
  });

  initSplashModal();
}

/* ---------- NEWS SPLASH MODAL ---------- */
function initSplashModal() {
  const news = getData('news') || [];
  const todayStr = new Date().toISOString().slice(0, 10);

  const splashItem = news.find(item => {
    if (item.active === false) return false;
    if (item.expiryDate && item.expiryDate < todayStr) return false;
    return item.isSplash === true;
  });

  if (!splashItem) {
    const existing = document.getElementById('newsSplashOverlay');
    if (existing) existing.remove();
    return;
  }

  const dismissedId = sessionStorage.getItem('cati_splash_dismissed');
  if (dismissedId === splashItem.id) return;

  let overlay = document.getElementById('newsSplashOverlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'newsSplashOverlay';
    overlay.className = 'news-splash-overlay';
    document.body.appendChild(overlay);
  }

  const resolvedImg = resolveImageUrl(splashItem.image);

  overlay.innerHTML = `
    <div class="news-splash-card" role="dialog" aria-modal="true" aria-label="Important Announcement">
      <button type="button" class="news-splash-close" id="btnCloseNewsSplash" aria-label="Close Announcement">&times;</button>

      <div class="news-splash-header">
        <span class="news-splash-badge"><i class="fa-solid fa-bullhorn"></i> Important Announcement</span>
        <div class="news-splash-img-wrap">
          <img src="${resolvedImg}" alt="${escapeHtml(splashItem.title)}" class="news-splash-img">
        </div>
      </div>

      <div class="news-splash-body">
        <div class="news-splash-meta"><i class="fa-regular fa-calendar-days"></i> ${escapeHtml(splashItem.date)} &bull; CATI Pakistan</div>
        <h2 class="news-splash-title">${escapeHtml(splashItem.title)}</h2>
        <p class="news-splash-desc">${escapeHtml(splashItem.description)}</p>

        <div class="news-splash-actions">
          <a href="news.html" class="btn btn--primary btn--sm">Read News & Events <i class="fa-solid fa-arrow-right" style="margin-left:0.3rem"></i></a>
          <button type="button" class="btn btn--outline btn--sm" id="btnDismissNewsSplash">Close</button>
        </div>
      </div>
    </div>
  `;

  requestAnimationFrame(() => {
    overlay.classList.add('active');
  });

  const closeSplash = () => {
    overlay.classList.remove('active');
    sessionStorage.setItem('cati_splash_dismissed', splashItem.id);
    setTimeout(() => overlay.remove(), 350);
  };

  overlay.querySelector('#btnCloseNewsSplash')?.addEventListener('click', closeSplash);
  overlay.querySelector('#btnDismissNewsSplash')?.addEventListener('click', closeSplash);

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeSplash();
  });
}

/* ---------- ENQUIRY FORM ---------- */
function renderEnquiryForm() {
  const fields = getData('enquiryFields');
  if (!fields) return;

  const formContainer = document.getElementById('enquiryFormFields');
  if (!formContainer) return;

  // Check URL for pre-filled course
  const urlParams = new URLSearchParams(window.location.search);
  const courseName = urlParams.get('course') || '';

  const visibleFields = fields.filter(f => f.visible);
  let html = '';
  let inRow = false;
  let rowFields = [];

  function flushRow() {
    if (rowFields.length === 0) return;
    if (rowFields.length === 1 && rowFields[0].halfWidth) {
      html += `<div class="grid grid--2"><div>${rowFields[0].html}</div><div></div></div>`;
    } else if (rowFields.length >= 2) {
      html += `<div class="grid grid--2">${rowFields.map(f => `<div>${f.html}</div>`).join('')}</div>`;
    } else {
      html += rowFields[0].html;
    }
    rowFields = [];
  }

  visibleFields.forEach(field => {
    let fieldHtml = '';
    const isRequired = field.required ? 'required' : '';
    const requiredMark = field.required ? ' *' : '';
    let defaultValue = '';

    // Pre-fill subject with course name
    if (field.name === 'subject' && courseName) {
      defaultValue = `Course Enrollment: ${courseName}`;
    }

    if (field.type === 'textarea') {
      flushRow();
      fieldHtml = `
        <div class="form-group">
          <label class="form-label" for="${field.name}">${escapeHtml(field.label)}${requiredMark}</label>
          <textarea id="${field.name}" name="${field.name}" class="form-textarea" ${isRequired} placeholder="${escapeHtml(field.placeholder || '')}">${escapeHtml(defaultValue)}</textarea>
          <span class="form-error">Required</span>
        </div>`;
      html += fieldHtml;
    } else if (field.type === 'select') {
      const options = (field.options || []).map(o =>
        `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`
      ).join('');
      fieldHtml = `
        <div class="form-group">
          <label class="form-label" for="${field.name}">${escapeHtml(field.label)}${requiredMark}</label>
          <select id="${field.name}" name="${field.name}" class="form-select" ${isRequired}>${options}</select>
        </div>`;
      if (field.halfWidth) {
        rowFields.push({ html: fieldHtml, halfWidth: true });
        if (rowFields.length === 2) flushRow();
      } else {
        flushRow();
        html += fieldHtml;
      }
    } else {
      fieldHtml = `
        <div class="form-group">
          <label class="form-label" for="${field.name}">${escapeHtml(field.label)}${requiredMark}</label>
          <input type="${field.type}" id="${field.name}" name="${field.name}" class="form-input" ${isRequired} placeholder="${escapeHtml(field.placeholder || '')}" value="${escapeHtml(defaultValue)}">
          <span class="form-error">Required</span>
        </div>`;
      if (field.halfWidth) {
        rowFields.push({ html: fieldHtml, halfWidth: true });
        if (rowFields.length === 2) flushRow();
      } else {
        flushRow();
        html += fieldHtml;
      }
    }
  });
  flushRow();

  formContainer.innerHTML = html;
}

/* ---------- HERO ---------- */
function renderHero() {
  let slides = getData('heroSlides');
  if (!slides || !Array.isArray(slides) || slides.length === 0) return;

  // Ensure first slide is video if CATI.mp4 exists
  if (!slides.some(s => s.type === 'video' || (s.src && typeof s.src === 'string' && s.src.endsWith('.mp4')))) {
    slides.unshift({ type: 'video', src: './CATI.mp4', alt: 'CATI Campus Video' });
  }

  const carousel = document.querySelector('.hero__carousel[data-dynamic]');
  if (!carousel) return;

  carousel.innerHTML = slides.map((s, i) => {
    const isActive = (i === 0);
    const captionHtml = (s.title || s.subtitle) ? `
      <div class="carousel-caption" style="position:absolute;bottom:2.5rem;left:2.5rem;z-index:3;color:#fff;background:rgba(15,23,42,0.75);padding:1rem 1.5rem;border-radius:10px;max-width:500px;backdrop-filter:blur(6px);border:1px solid rgba(255,255,255,0.15);box-shadow:0 10px 25px rgba(0,0,0,0.3)">
        ${s.title ? `<h3 style="margin:0 0 0.4rem;font-size:1.4rem;font-weight:700;color:#fff">${escapeHtml(s.title)}</h3>` : ''}
        ${s.subtitle ? `<p style="margin:0;font-size:0.95rem;color:rgba(241,245,249,0.9);line-height:1.4">${escapeHtml(s.subtitle)}</p>` : ''}
      </div>
    ` : '';

    const resolvedSrc = resolveImageUrl(s.src);

    if (s.type === 'video' || (s.src && typeof s.src === 'string' && s.src.endsWith('.mp4'))) {
      const defaultPoster = resolveImageUrl('./src/assets/images/hero-campus.png');
      return `
        <div class="carousel-slide${isActive ? ' active' : ''}">
          <video autoplay muted loop playsinline disablePictureInPicture poster="${defaultPoster}" preload="auto" style="width:100%;height:100%;object-fit:cover;pointer-events:none;" data-cached-video="${resolvedSrc}">
            <source src="${resolvedSrc}" type="video/mp4">
          </video>
          ${captionHtml}
        </div>
      `;
    }
    return `
      <div class="carousel-slide${isActive ? ' active' : ''}">
        <img src="${resolvedSrc}" alt="${escapeHtml(s.alt || '')}" ${isActive ? 'loading="eager"' : 'loading="lazy"'}>
        ${captionHtml}
      </div>
    `;
  }).join('');

  carousel.querySelectorAll('video[data-cached-video]').forEach(v => {
    const src = v.getAttribute('data-cached-video');
    setupCachedVideo(v, src);
  });

  try {
    initCarousel();
  } catch (e) {
    console.warn('Error re-initializing carousel after hero render:', e);
  }
}

/* ---------- SCHOOLS ---------- */
function renderSchools() {
  const schools = getData('schools');
  if (!schools) return;

  const grid = document.querySelector('.schools-grid[data-dynamic]');
  if (grid) {
    grid.innerHTML = schools.map((s, i) => `
      <div class="school-card reveal reveal-delay-${i + 1}">
        <div class="school-card__icon"><i class="${s.icon}"></i></div>
        <h3 class="school-card__title">${escapeHtml(s.title)}</h3>
        <p class="school-card__desc">${escapeHtml(s.description)}</p>
        <a href="${s.link}" class="school-card__link">Learn More <i class="fa-solid fa-arrow-right"></i></a>
      </div>
    `).join('');
  }

  // Dynamically sync schools.html sections with data store
  schools.forEach(s => {
    const sec = document.getElementById(s.id);
    if (sec) {
      const h2 = sec.querySelector('h2');
      if (h2 && s.title) h2.textContent = s.title;
      const p = sec.querySelector('p');
      if (p && s.description) p.textContent = s.description;
    }
  });
}

/* ---------- STATS ---------- */
function renderStats() {
  const stats = getData('stats');
  if (!stats) return;

  const container = document.querySelector('.stats-bar__inner[data-dynamic]');
  if (!container) return;

  container.innerHTML = stats.map((s, i) => `
    <div class="stat-item reveal${i > 0 ? ` reveal-delay-${i}` : ''}">
      <div class="stat-item__value" data-counter="${s.value}" data-suffix="${s.suffix}">${s.value}${s.suffix}</div>
      <div class="stat-item__label">${escapeHtml(s.label)}</div>
    </div>
  `).join('');
}

/* ---------- ABOUT ---------- */
function renderAbout() {
  const about = getData('about');
  if (!about) return;

  const section = document.querySelector('.about-preview[data-dynamic]');
  if (!section) return;

  section.innerHTML = `
    <div class="grid grid--6-4" style="align-items:center">
      <div class="reveal">
        <span class="section-header__eyebrow" style="text-align:left">${escapeHtml(about.eyebrow)}</span>
        <h2 style="margin-bottom:var(--space-lg)">${escapeHtml(about.title)}</h2>
        <p style="font-size:var(--fs-body-lg); color:var(--clr-text-muted)">${escapeHtml(about.paragraph1)}</p>
        <p style="color:var(--clr-text-muted)">${escapeHtml(about.paragraph2)}</p>
        <a href="about.html" class="btn btn--primary" style="margin-top:var(--space-lg)">Discover Our Heritage</a>
      </div>
      <div class="reveal reveal-delay-2">
        <div class="img-zoom" style="border-radius:var(--radius-lg)">
          <img src="${about.image}" alt="CATI campus aerial view, Hyderabad Pakistan" style="border-radius:var(--radius-lg)">
        </div>
      </div>
    </div>`;
}

/* ---------- QUICK LINKS ---------- */
export function renderQuickLinks() {
  let quickLinks = getData('quickLinks');
  if (!quickLinks || !Array.isArray(quickLinks)) {
    quickLinks = getDefaults('quickLinks') || [];
  }

  const activeLinks = quickLinks
    .filter(link => link && link.isActive !== false)
    .sort((a, b) => (Number(a.numbering) || 99) - (Number(b.numbering) || 99));

  // Update desktop Quick Links dropdown menus
  document.querySelectorAll('.quick-links-menu').forEach(menu => {
    if (activeLinks.length === 0) {
      menu.innerHTML = '<span style="display:block;padding:0.75rem 1rem;font-size:0.85rem;color:var(--clr-text-muted)">No quick links available.</span>';
      return;
    }

    menu.innerHTML = activeLinks.map(link => {
      let iconClass = 'fa-solid fa-link';
      const type = String(link.fileType || '').toLowerCase();
      if (type.includes('pdf')) iconClass = 'fa-solid fa-file-pdf';
      else if (type.includes('doc')) iconClass = 'fa-solid fa-file-word';
      else if (type.includes('xls') || type.includes('excel')) iconClass = 'fa-solid fa-file-excel';
      else if (link.file) iconClass = 'fa-solid fa-paperclip';

      const href = link.file || link.link || '#';
      const isExternal = href.startsWith('http://') || href.startsWith('https://') || link.file;
      const targetAttr = isExternal ? 'target="_blank" rel="noopener"' : '';
      const badgeHtml = link.fileType ? `<span class="badge-file">${escapeHtml(link.fileType)}</span>` : '';

      return `
        <a href="${escapeHtml(href)}" ${targetAttr} class="quick-link-item">
          <i class="${iconClass}" style="width:16px;color:var(--clr-primary);font-size:0.85rem"></i>
          <span>${escapeHtml(link.title)}</span>
          ${badgeHtml}
        </a>
      `;
    }).join('');
  });

  // Update mobile drawer quick links
  document.querySelectorAll('.mobile-quick-links-list').forEach(container => {
    if (activeLinks.length === 0) {
      container.innerHTML = '';
      return;
    }
    container.innerHTML = activeLinks.map(link => {
      const href = link.file || link.link || '#';
      const isExternal = href.startsWith('http://') || href.startsWith('https://') || link.file;
      const targetAttr = isExternal ? 'target="_blank" rel="noopener"' : '';
      return `
        <a href="${escapeHtml(href)}" ${targetAttr} style="padding-left:1.2rem;font-size:0.88rem;display:flex;align-items:center;gap:0.5rem">
          <i class="fa-solid fa-angle-right" style="font-size:0.7rem;color:var(--clr-accent)"></i>
          <span>${escapeHtml(link.title)}</span>
        </a>
      `;
    }).join('');
  });
}
